import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import BasicModal from '../../modules/modals/BasicModal';
import Headline from '../common/Headline';
import { SITE_NAME } from '../../config/Master';
import { isDarkMode } from '../../utils/helpers';
import Avatar from '../../atoms/Avatar';
import colors from '../../theme/colors';
import ContextualButton from '../../atoms/buttons/ContextualButton';
import ArrowRightIcon from '../../atoms/icons/ArrowRightIcon';
import ArrowLeftIcon from '../../atoms/icons/ArrowLeftIcon';
import RadioButton from '../../atoms/PackageRadioButton';
import TextArea from '../../atoms/TextArea';
import { getBackgroundTheme, getBorders } from '../../style';
import { reportUser } from '../../actions/profileActions';
import { useDisableBodyElementScroll } from '../../hooks/useDisableBodyScroll';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { CancelRounded } from '@material-ui/icons';
import { SvgIcon } from '@material-ui/core';
import { CloseBtn, closeButton } from '../common/CommonStyle';
import { CustomStyledProps } from 'style/style';

const showReasonListAnimation = keyframes`
  0% {
    transform: translateX(30%);
    opacity: 0;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
`;

const showCategoryListAnimation = keyframes`
  0% {
    transform: translateX(-30%);
    opacity: 0;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
`;

const slideUpAnimation = keyframes`
  from {
    transform: translateY(40%);
    opacity: 0;
  }
  to {
    transform: translateY(0%);
    opacity: 1;
  }
`;

const avatarCustomStyle = {
  flex: '0 0 100px',
  height: 100,
  borderRadius: 10,
};

const WrapperStyle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const headlineStyle = {
  fontWeight: 600,
  letterSpacing: '-0.44px',
  margin: '10px 0px',
};

const CaptionWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const Caption = styled('p')(() => ({
  margin: 0,
  letterSpacing: '-0.3px',
  color: isDarkMode() ? colors.dark.black.secondary : colors.light.gray.light,
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ReasonContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}));

const CategoryDiv = styled('div')(() => ({
  display: 'flex',
  height: '20px',
  padding: ' 10px 17px 10px 15px',
  justifyContent: 'center',
  alignItems: 'flex-start',
  borderRadius: 3,
  border: `1px solid ${!isDarkMode() ? '#DAE1E9' : '#6c6c6c'}`,
  background: isDarkMode() ? '#020d15' : '#F6F4F4',
}));

const DescriptionDiv = styled('div')({
  width: '95%',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
});

const IconWrapper = styled('div')({
  width: '5%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const MainTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '10px',
});

const CategoryWrapper = styled('div')(
  {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    transition: 'transform 0.2s ease-out',
  },
  ({ isAnimate }: CustomStyledProps) =>
    isAnimate && {
      animation: `${showCategoryListAnimation} 0.2s ease-out`,
    }
);

const ReasonWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-out',
  animation: `${showReasonListAnimation} 0.2s ease-out`,
});

const TextAreaWrapper = styled('div')({
  transition: 'transform 0.3s ease-out',
  animation: `${slideUpAnimation} 0.3s ease-out`,
});

const ButtonWrapper = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  marginTop: 10,
}));

const BottomText = styled('span')(() => ({
  width: '50%',
  fontStyle: 'italic',
  letterSpacing: '-0.28px',
  fontSize: '14px',
  color: isDarkMode() ? '#6A6A6A' : '#8F8F8F',
}));

interface ReportProps {
  showReportModal: boolean;
  toggleReportModal: () => void;
  hashId: string;
  username: string;
  avatar: string;
  reasons: any;
}

const ReportModal = ({ showReportModal, toggleReportModal, hashId, username, avatar, reasons }: ReportProps): any => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [isAnimateCategory, setAnimateCategory] = useState(false);
  const [showDescError, setShowDescError] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const textAreaRef = useRef(null);

  useDisableBodyElementScroll(showReportModal);

  const handleMsg = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    setShowDescError(isEmpty(value));
    setMessage(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isEmpty(message)) {
      setShowDescError(isEmpty(message));
      return;
    }

    setSubmitting(true);

    const reportData = {
      submitting,
      reason_id: selectedReason,
      message: message,
      account: hashId,
      username: username,
    };

    // send mixpanel event for abused reports
    handleMixpanelEvent();

    dispatch(reportUser(reportData, navigate));
  };

  const handleMixpanelEvent = () => {
    if (!isEmpty(selectedCategory?.reasons)) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.ABUSE_REPORTED,
        payload: {
          eventProperties: {
            subject: selectedCategory.reasons.find(item => item.slug === selectedReason)?.description,
          },
        },
      });
    }
  };

  const handleCategorySelect = (item: any) => {
    setTimeout(() => {
      setSelectedCategory(item);
    }, 100);
  };
  const handleReasonChange = (item: any) => {
    setSelectedReason(item.slug);

    if (item.slug) {
      textAreaRef?.current?.focus();
    }
  };

  const renderCategory = (item: any) => {
    return (
      <CategoryDiv key={item.slug} onClick={() => handleCategorySelect(item)} style={{ cursor: 'pointer' }}>
        <DescriptionDiv>
          <Caption style={{ fontSize: 15 }}>{item.description}</Caption>
        </DescriptionDiv>
        <IconWrapper>
          <ArrowRightIcon fillColor={isDarkMode() ? colors.dark.black.secondary : colors.light.gray.light} />
        </IconWrapper>
      </CategoryDiv>
    );
  };

  const renderReason = item => {
    const activeReason = selectedReason === item.slug;

    return (
      <CategoryDiv
        key={item.slug}
        style={{
          padding: ' 10px 19px 10px 15px',
          opacity: activeReason || isEmpty(selectedReason) ? 1 : 0.5,
          cursor: 'pointer',
        }}
        onClick={() => handleReasonChange(item)}
      >
        <DescriptionDiv>
          <Caption style={{ fontSize: 15 }}>{item.description}</Caption>
        </DescriptionDiv>
        <IconWrapper>
          <RadioButton
            label={``}
            id={`reason_${item.slug}`}
            name={'Reason'}
            labelId={`reason_${item.slug}`}
            checked={Boolean(activeReason)}
            onRadioChange={() => handleReasonChange(item)}
            style={{ cursor: 'pointer' }}
          />
        </IconWrapper>
      </CategoryDiv>
    );
  };

  const handleClose = () => {
    toggleReportModal();
    setSelectedCategory(null);
    setSelectedReason(null);
    setMessage(null);
  };

  const handleCategoryTitleClick = () => {
    setSelectedReason(null);
    setSelectedCategory(null);
    setMessage('');
    setAnimateCategory(true);
    setShowDescError(false);
  };

  return (
    <BasicModal
      isOpen={showReportModal}
      hideClose={true}
      customStyle={{
        content: {
          borderRadius: '10px',
          padding: '26px 30px',
          maxWidth: 600,
          backgroundColor: getBackgroundTheme().block2,
        },
      }}
      onClose={handleClose}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      <CloseBtn data-test-id={`confirm-upload-close`} onClick={handleClose}>
        <SvgIcon component={CancelRounded} style={closeButton} />
      </CloseBtn>
      <WrapperStyle>
        <Avatar src={avatar} width="100px" alt="Avatar photo" customStyle={avatarCustomStyle} />
        <Headline style={headlineStyle}>Report {username}</Headline>
        <Caption className="pageHeader-description">
          We review every case individually and will take appropriate action. Thank you for helping improve {SITE_NAME}.
        </Caption>
        {isEmpty(selectedCategory) ? (
          <CaptionWrapper>
            <Caption
              style={{
                fontWeight: 600,
                margin: '10px 0px',
              }}
            >
              Reason for Reporting
            </Caption>

            <CategoryWrapper isAnimate={isAnimateCategory}>
              {reasons.length > 0 && reasons.map(item => renderCategory(item))}
            </CategoryWrapper>
          </CaptionWrapper>
        ) : (
          <ReasonContainer>
            <MainTitle onClick={handleCategoryTitleClick}>
              <ArrowLeftIcon />
              <Caption
                style={{
                  fontWeight: 600,
                }}
              >
                {selectedCategory?.description}
              </Caption>
            </MainTitle>

            <ReasonWrapper style={{ gap: isEmpty(selectedCategory) ? 0 : 10 }}>
              {!isEmpty(selectedCategory?.reasons) && selectedCategory.reasons.map(item => renderReason(item))}
            </ReasonWrapper>

            <TextAreaWrapper>
              <TextArea
                ref={textAreaRef}
                inputLabel={'Description'}
                value={message}
                rows={5}
                placeholder="Describe the issue here"
                resize="none"
                onChange={handleMsg}
                isRequired={true}
                customStyle={{
                  margin: isEmpty(selectedCategory.reasons) ? '-10px 0 -10px 0' : '10px 0 -10px 0',
                  paddingBottom: 0,
                }}
                status={showDescError ? 'error' : ''}
                statusMessage={showDescError ? 'You must submit a description to file a report' : ''}
                customLabelStyle={{ fontSize: '14px' }}
                customInputStyle={{
                  borderRadius: '3px',
                  border: isDarkMode() ? 'none' : getBorders().DEFAULT,
                  background: isDarkMode() ? '#113956' : '#FFF',
                  boxShadow: isDarkMode() ? 'none' : '1px 1px 3px 0px rgba(0, 0, 0, 0.20) inset',
                }}
              />
            </TextAreaWrapper>
          </ReasonContainer>
        )}

        <ButtonWrapper>
          <BottomText>Your report is private</BottomText>
          {username && (
            <ContextualButton
              type="button"
              id="report-user"
              disabled={
                isEmpty(selectedCategory) ||
                (!isEmpty(selectedCategory?.reasons) && !selectedReason) ||
                !message ||
                submitting
              }
              loading={submitting}
              buttonText={'Send Report'}
              buttonWrapperStyle={{ width: '50%', textAlign: 'right' }}
              customStyle={{
                padding: '12px 22px',
              }}
              onClick={handleSubmit}
            />
          )}
        </ButtonWrapper>
      </WrapperStyle>
    </BasicModal>
  );
};

export default ReportModal;
